/**
 * Closed Container Repair - reopens completed / canceled projects and lists that still hold open
 * to-dos.
 *
 * Such a project is invisible work: Today and Upcoming hide the to-dos of a closed container.
 * closeContainer never makes one, but they exist from before the rule (the old sidebar close left
 * the to-dos open), from two devices (one closes, the other adds a to-do offline) and from stale
 * clients. The shared op reopens the container alone (a new statusAt, no undo: a repair is not the
 * user's action).
 *
 * Runs once after the initial sync (a close whose to-dos have not arrived yet looks the same as a
 * broken one), at most once a day per device (setting_local).
 */

import { repairClosedContainersWithOpenTasks } from "@totodo/operations"
import type { BackgroundTaskDef } from "@/background/types"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"
import { db } from "@/db/schema"

const LAST_RUN_KEY = "#closed-container-repair-last-run"
const THROTTLE_MS = 24 * 60 * 60 * 1000

async function lastRun(): Promise<number> {
  try {
    const row = await db.setting_local.get(LAST_RUN_KEY)
    return Number(row?.value) || 0
  } catch {
    return 0
  }
}

async function saveRun(): Promise<void> {
  const timestamp = new Date().toISOString()
  await db.setting_local.put({
    id: LAST_RUN_KEY,
    value: String(Date.now()),
    device: "local",
    createdAt: timestamp,
    createdBy: "local",
    updatedAt: timestamp,
    updatedBy: "local",
  })
}

export const closedContainerRepairTask: BackgroundTaskDef = {
  id: "closed-container-repair",
  description: "Reopen completed/canceled projects and lists that still have open to-dos",
  priority: 12, // After migrations, day change and index cleanup; before the file GC
  triggers: ["app-start"],
  initialDelayMs: 20_000, // let the first sync settle

  async run(_signal) {
    const synced = await waitForInitialSync(db)
    if (!synced) return
    if (Date.now() - (await lastRun()) < THROTTLE_MS) return

    const result = await repairClosedContainersWithOpenTasks()
    if (!result.ok) {
      console.warn("[ClosedContainerRepair] failed:", result.error)
      return
    }
    await saveRun()
    if (result.data.reopened.length > 0) {
      console.info(
        `[ClosedContainerRepair] Reopened ${result.data.reopened.length} closed project(s)/list(s) with open to-dos`,
      )
    }
  },
}
